import { toast } from 'sonner';
import axiosInstance from '../axiosInstance';
import { financePath } from '../../../utils/financePath';

// Sub Petty Cash — Manufacturer only. Mirrors PettyCashAction.js's shape
// (same action-type naming convention, same dispatch pattern) so the two
// modules stay easy to read side by side, but talks to the brand-new
// sub-petty-cash endpoints (manufacturerFinance.js) instead.

// Action Types
export const FETCH_SUB_PETTY_CASH_REQUEST = 'FETCH_SUB_PETTY_CASH_REQUEST';
export const FETCH_SUB_PETTY_CASH_SUCCESS = 'FETCH_SUB_PETTY_CASH_SUCCESS';
export const FETCH_SUB_PETTY_CASH_FAILURE = 'FETCH_SUB_PETTY_CASH_FAILURE';

export const CREATE_SUB_PETTY_CASH_REQUEST = 'CREATE_SUB_PETTY_CASH_REQUEST';
export const CREATE_SUB_PETTY_CASH_SUCCESS = 'CREATE_SUB_PETTY_CASH_SUCCESS';
export const CREATE_SUB_PETTY_CASH_FAILURE = 'CREATE_SUB_PETTY_CASH_FAILURE';

export const UPDATE_SUB_PETTY_CASH_REQUEST = 'UPDATE_SUB_PETTY_CASH_REQUEST';
export const UPDATE_SUB_PETTY_CASH_SUCCESS = 'UPDATE_SUB_PETTY_CASH_SUCCESS';
export const UPDATE_SUB_PETTY_CASH_FAILURE = 'UPDATE_SUB_PETTY_CASH_FAILURE';

export const DELETE_SUB_PETTY_CASH_REQUEST = 'DELETE_SUB_PETTY_CASH_REQUEST';
export const DELETE_SUB_PETTY_CASH_SUCCESS = 'DELETE_SUB_PETTY_CASH_SUCCESS';
export const DELETE_SUB_PETTY_CASH_FAILURE = 'DELETE_SUB_PETTY_CASH_FAILURE';

export const FETCH_SUB_PETTY_CASH_DAILY_FLOW_REQUEST = 'FETCH_SUB_PETTY_CASH_DAILY_FLOW_REQUEST';
export const FETCH_SUB_PETTY_CASH_DAILY_FLOW_SUCCESS = 'FETCH_SUB_PETTY_CASH_DAILY_FLOW_SUCCESS';
export const FETCH_SUB_PETTY_CASH_DAILY_FLOW_FAILURE = 'FETCH_SUB_PETTY_CASH_DAILY_FLOW_FAILURE';

// Fetch Sub Petty Cash Transactions (All Transactions tab)
export const Fetch_SubPettyCash =
  (params = {}) =>
    async (dispatch) => {
      dispatch({ type: FETCH_SUB_PETTY_CASH_REQUEST });

      try {
        const queryString = new URLSearchParams(params).toString();
        const response = await axiosInstance.get(financePath(`sub-petty-cash?${queryString}`));

        dispatch({
          type: FETCH_SUB_PETTY_CASH_SUCCESS,
          payload: response.data.data
        });

        return response.data;
      } catch (error) {
        const errorMsg = error.response?.data?.error || 'Failed to fetch sub petty cash transactions';
        dispatch({
          type: FETCH_SUB_PETTY_CASH_FAILURE,
          payload: errorMsg
        });
        toast.error(errorMsg);
        throw error;
      }
    };

// Fetch Sub Petty Cash Daily Cash Flow (Cash Flow tab — opening/added/used/closing)
export const Fetch_SubPettyCashDailyFlow =
  (params = {}) =>
    async (dispatch) => {
      dispatch({ type: FETCH_SUB_PETTY_CASH_DAILY_FLOW_REQUEST });

      try {
        const queryString = new URLSearchParams(params).toString();
        const response = await axiosInstance.get(financePath(`sub-petty-cash/balance/daily-flow?${queryString}`));

        dispatch({
          type: FETCH_SUB_PETTY_CASH_DAILY_FLOW_SUCCESS,
          payload: response.data.data
        });

        return response.data;
      } catch (error) {
        const errorMsg = error.response?.data?.error || 'Failed to fetch daily cash flow';
        dispatch({
          type: FETCH_SUB_PETTY_CASH_DAILY_FLOW_FAILURE,
          payload: errorMsg
        });
        toast.error(errorMsg);
        throw error;
      }
    };

// Create a normal Sub Petty Cash entry — cash_out (reason required, e.g.
// "chai for guest") or cash_in (manual addition, not linked to Finance Cash).
// POST /sub-petty-cash
export const Create_SubPettyCash = (data) => async (dispatch) => {
  dispatch({ type: CREATE_SUB_PETTY_CASH_REQUEST });

  try {
    const response = await axiosInstance.post(financePath('sub-petty-cash'), data);

    dispatch({
      type: CREATE_SUB_PETTY_CASH_SUCCESS,
      payload: response.data.data
    });

    toast.success('Sub Petty Cash transaction created successfully');
    return response.data;
  } catch (error) {
    const errorMsg = error.response?.data?.error || 'Failed to create transaction';
    dispatch({
      type: CREATE_SUB_PETTY_CASH_FAILURE,
      payload: errorMsg
    });
    toast.error(errorMsg);
    throw error;
  }
};

// Update a normal cash_in / cash_out entry (not fund_in/hand_over — see
// SubPettyCashController.update's own rejection for those).
// PUT /sub-petty-cash/:id
export const Update_SubPettyCash = (id, data) => async (dispatch) => {
  dispatch({ type: UPDATE_SUB_PETTY_CASH_REQUEST });

  try {
    const response = await axiosInstance.put(financePath(`sub-petty-cash/${id}`), data);

    dispatch({
      type: UPDATE_SUB_PETTY_CASH_SUCCESS,
      payload: response.data.data
    });

    toast.success('Sub Petty Cash transaction updated successfully');
    return response.data;
  } catch (error) {
    const errorMsg = error.response?.data?.error || 'Failed to update transaction';
    dispatch({
      type: UPDATE_SUB_PETTY_CASH_FAILURE,
      payload: errorMsg
    });
    toast.error(errorMsg);
    throw error;
  }
};

// Delete a normal cash_in / cash_out entry (not fund_in/hand_over).
// DELETE /sub-petty-cash/:id
export const Delete_SubPettyCash = (id) => async (dispatch) => {
  dispatch({ type: DELETE_SUB_PETTY_CASH_REQUEST });

  try {
    const response = await axiosInstance.delete(financePath(`sub-petty-cash/${id}`));

    dispatch({
      type: DELETE_SUB_PETTY_CASH_SUCCESS,
      payload: id
    });

    toast.success('Sub Petty Cash transaction deleted successfully');
    return response.data;
  } catch (error) {
    const errorMsg = error.response?.data?.error || 'Failed to delete transaction';
    dispatch({
      type: DELETE_SUB_PETTY_CASH_FAILURE,
      payload: errorMsg
    });
    toast.error(errorMsg);
    throw error;
  }
};
// cash"). Same endpoint whether triggered from this page's "Fund In" option
// or from Finance Cash's "Send to Sub Petty Cash" option.
// POST /sub-petty-cash/fund-transfer
export const FundTransfer_SubPettyCash = (data) => async (dispatch) => {
  dispatch({ type: CREATE_SUB_PETTY_CASH_REQUEST });

  try {
    const response = await axiosInstance.post(financePath('sub-petty-cash/fund-transfer'), data);

    dispatch({
      type: CREATE_SUB_PETTY_CASH_SUCCESS,
      payload: response.data.data
    });

    toast.success('Fund transferred to Sub Petty Cash successfully');
    return response.data;
  } catch (error) {
    const errorMsg = error.response?.data?.error || 'Fund transfer failed';
    dispatch({
      type: CREATE_SUB_PETTY_CASH_FAILURE,
      payload: errorMsg
    });
    toast.error(errorMsg);
    throw error;
  }
};

// Hand Over: Sub Petty Cash -> Finance Cash ("return leftover/settle back").
// Same endpoint whether triggered from this page's "Hand Over" option or
// from Finance Cash's "Receive from Sub Petty Cash" option.
// POST /sub-petty-cash/hand-over
export const HandOver_SubPettyCash = (data) => async (dispatch) => {
  dispatch({ type: CREATE_SUB_PETTY_CASH_REQUEST });

  try {
    const response = await axiosInstance.post(financePath('sub-petty-cash/hand-over'), data);

    dispatch({
      type: CREATE_SUB_PETTY_CASH_SUCCESS,
      payload: response.data.data
    });

    toast.success('Handed over to Finance Cash successfully');
    return response.data;
  } catch (error) {
    const errorMsg = error.response?.data?.error || 'Hand over failed';
    dispatch({
      type: CREATE_SUB_PETTY_CASH_FAILURE,
      payload: errorMsg
    });
    toast.error(errorMsg);
    throw error;
  }
};